import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useSession } from '../session';

interface Preview {
  tenantName: string;
  email: string;
  role: string;
  existingUser: boolean;
  signedInAsInvitee: boolean;
}

export function AcceptInvite() {
  const { token = '' } = useParams();
  const { refresh, productName } = useSession();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<Preview>(`/api/invitations/${encodeURIComponent(token)}`)
      .then(setPreview)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'This invitation could not be loaded'));
  }, [token]);

  async function accept(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ next: 'mfa' | 'enrol'; tenantId: string }>(`/api/invitations/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        body: preview?.existingUser ? {} : { displayName, password },
      });
      await refresh();
      navigate(result.next === 'enrol' ? '/enrol' : `/workspaces/${result.tenantId}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'login_required') {
        navigate('/login', { state: { returnTo: `/invite/${token}` } });
        return;
      }
      setError(err instanceof ApiError ? err.message : 'The invitation could not be accepted');
    } finally {
      setBusy(false);
    }
  }

  if (error && !preview) {
    return (
      <div className="card narrow">
        <h1>Invitation</h1>
        <div className="alert">{error}</div>
        <Link to="/login" className="button block">Go to sign in</Link>
      </div>
    );
  }
  if (!preview) return <p className="muted">Loading…</p>;

  return (
    <div className="card narrow">
      <h1>Join {preview.tenantName}</h1>
      <p className="lede">You have been invited to {preview.tenantName} on {productName} as {preview.role}, using {preview.email}.</p>
      {error && <div className="alert">{error}</div>}
      {preview.existingUser ? (
        <>
          <p>{preview.signedInAsInvitee ? 'You are signed in as this account.' : 'You already have an account with this email. Sign in to accept.'}</p>
          <button type="button" className="block" onClick={() => accept()} disabled={busy}>
            {preview.signedInAsInvitee ? 'Accept invitation' : 'Sign in and accept'}
          </button>
        </>
      ) : (
        <form onSubmit={accept}>
          <label>
            Your name
            <input id="invite-name" required maxLength={120} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </label>
          <label>
            Choose a password
            <input id="invite-password" type="password" autoComplete="new-password" required minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} />
            <span className="small muted">At least 12 characters. It is checked against known breaches.</span>
          </label>
          <button type="submit" className="block" disabled={busy}>{busy ? 'Creating account…' : 'Create account and join'}</button>
        </form>
      )}
    </div>
  );
}
